import React from 'react';
import { 
  DocumentTextIcon, 
  UserGroupIcon, 
  CurrencyEuroIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { Invoice, Client } from '../types';
import { YearlyIncomeChart } from './YearlyIncomeChart';

interface DashboardStatsProps {
  invoices: Invoice[];
  clients: Client[];
  apiStats?: { expenseCount?: number; expenseTotal?: number; receivedInvoiceCount?: number };
  premium?: boolean;
}

// Helper function to get quarter months
const getQuarterMonths = (quarter: number): string => {
  const quarters = {
    1: 'Ene-Mar',
    2: 'Abr-Jun', 
    3: 'Jul-Sep',
    4: 'Oct-Dic'
  };
  return quarters[quarter as keyof typeof quarters] || '';
};

export const DashboardStats: React.FC<DashboardStatsProps> = ({ invoices, clients, apiStats, premium }) => {
  const now = new Date();
  const thisMonth = now.getMonth();
  const thisYear = now.getFullYear();

  const totalInvoices = invoices.length;
  const paidInvoices = invoices.filter(inv => inv.status === 'paid').length;
  const overdueInvoices = invoices.filter(inv => inv.status === 'overdue').length;
  const draftInvoices = invoices.filter(inv => inv.status === 'draft').length;
  const sentInvoices = invoices.filter(inv => inv.status === 'sent').length;

  const monthlyRevenue = invoices
    .filter(inv => {
      const d = new Date(inv.date);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    })
    .reduce((sum, inv) => sum + Number(inv.total || 0), 0);

  const yearlyRevenue = invoices
    .filter(inv => new Date(inv.date).getFullYear() === thisYear)
    .reduce((sum, inv) => sum + Number(inv.total || 0), 0);

  const prevMonthRevenue = invoices
    .filter(inv => {
      const d = new Date(inv.date);
      const pm = thisMonth === 0 ? 11 : thisMonth - 1;
      const py = thisMonth === 0 ? thisYear - 1 : thisYear;
      return d.getMonth() === pm && d.getFullYear() === py;
    })
    .reduce((sum, inv) => sum + Number(inv.total || 0), 0);

  const monthDelta = prevMonthRevenue > 0
    ? ((monthlyRevenue - prevMonthRevenue) / prevMonthRevenue) * 100
    : null;

  const monthLabel = now.toLocaleDateString('es-ES', { month: 'long' });
  const monthLabelCap = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

  // Calcular IVA del trimestre actual
  const currentDate = new Date();
  const currentQuarter = Math.floor(currentDate.getMonth() / 3) + 1;
  const quarterStartMonth = (currentQuarter - 1) * 3;
  const quarterEndMonth = quarterStartMonth + 2;

  const quarterlyVAT = invoices
    .filter(inv => {
      const invoiceDate = new Date(inv.date);
      const invoiceMonth = invoiceDate.getMonth();
      const invoiceYear = invoiceDate.getFullYear();
      return invoiceYear === currentDate.getFullYear() &&
             invoiceMonth >= quarterStartMonth &&
             invoiceMonth <= quarterEndMonth &&
             inv.status === 'paid'; // Solo facturas pagadas
    })
    .reduce((sum, inv) => sum + (inv.ivaAmount || 0), 0);

  const topClients = clients.map(client => {
    const clientInvoices = invoices.filter(inv => inv.clientId === client.id);
    const totalRevenue = clientInvoices.reduce((sum, inv) => sum + Number(inv.total || 0), 0);
    return {
      client,
      totalRevenue,
      invoiceCount: clientInvoices.length
    };
  }).sort((a, b) => b.totalRevenue - a.totalRevenue).slice(0, 3);

  const expenseInfo = apiStats?.expenseCount != null ? [{
      name: 'Gastos registrados',
      value: apiStats.expenseCount || 0,
      icon: CurrencyEuroIcon,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      subtitle: apiStats.expenseTotal ? `${Number(apiStats.expenseTotal).toFixed(2)} €` : undefined
    }] : [];

  const stats = [
    {
      name: monthLabelCap,
      value: `${monthlyRevenue.toFixed(2)} €`,
      subtitle: monthDelta !== null
        ? `${monthDelta >= 0 ? '+' : ''}${monthDelta.toFixed(1)}% vs mes anterior`
        : `Ingresos mes actual`,
      subtitleColor: monthDelta !== null
        ? (monthDelta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400')
        : 'text-[#8898aa]',
      icon: CurrencyEuroIcon,
      iconColor: 'text-emerald-600',
      iconBg: 'bg-emerald-50 dark:bg-emerald-900/20',
    },
    {
      name: `Año ${thisYear}`,
      value: `${yearlyRevenue.toFixed(2)} €`,
      subtitle: `${totalInvoices} factura${totalInvoices !== 1 ? 's' : ''} emitida${totalInvoices !== 1 ? 's' : ''}`,
      subtitleColor: 'text-[#8898aa]',
      icon: CurrencyEuroIcon,
      iconColor: 'text-[#635BFF]',
      iconBg: 'bg-[#635BFF]/10',
    },
    {
      name: 'Cobradas',
      value: paidInvoices,
      subtitle: paidInvoices > 0 ? `${((paidInvoices / Math.max(totalInvoices, 1)) * 100).toFixed(0)}% del total` : '—',
      subtitleColor: 'text-[#8898aa]',
      icon: CheckCircleIcon,
      iconColor: 'text-emerald-600',
      iconBg: 'bg-emerald-50 dark:bg-emerald-900/20',
    },
    {
      name: 'Pendientes',
      value: draftInvoices + sentInvoices,
      subtitle: sentInvoices > 0 ? `${sentInvoices} enviada${sentInvoices !== 1 ? 's' : ''}` : 'Sin enviar',
      subtitleColor: 'text-[#8898aa]',
      icon: ClockIcon,
      iconColor: 'text-amber-600',
      iconBg: 'bg-amber-50 dark:bg-amber-900/20',
    },
    {
      name: 'Vencidas',
      value: overdueInvoices,
      subtitle: overdueInvoices > 0 ? 'Requieren atención' : 'Todo al día',
      subtitleColor: overdueInvoices > 0 ? 'text-red-500 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400',
      icon: ExclamationTriangleIcon,
      iconColor: overdueInvoices > 0 ? 'text-red-600' : 'text-emerald-600',
      iconBg: overdueInvoices > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-emerald-50 dark:bg-emerald-900/20',
    },
    {
      name: 'Clientes',
      value: clients.length,
      subtitle: `${totalInvoices > 0 ? (totalInvoices / Math.max(clients.length, 1)).toFixed(1) : '0'} fact/cliente`,
      subtitleColor: 'text-[#8898aa]',
      icon: UserGroupIcon,
      iconColor: 'text-[#635BFF]',
      iconBg: 'bg-[#635BFF]/10',
    },
    ...expenseInfo.map(e => ({
      ...e,
      subtitle: e.subtitle,
      subtitleColor: 'text-[#8898aa]',
      iconColor: 'text-orange-600',
      iconBg: 'bg-orange-50 dark:bg-orange-900/20',
    }))
  ];

  if (premium) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
        {stats.slice(0, 6).map((stat) => {
          const Icon = stat.icon;
          const isRevenueCard = stat.name === monthLabelCap || stat.name === `Año ${thisYear}`;
          return (
            <div
              key={stat.name}
              className={`rounded-xl border bg-white dark:bg-[#161b22] p-5 shadow-stripe hover:shadow-stripe-md transition-shadow ${
                isRevenueCard
                  ? 'border-[#635BFF]/20 dark:border-[#635BFF]/30'
                  : 'border-[#E6E9F0] dark:border-[#21262d]'
              }`}
            >
              <div className="flex items-start justify-between mb-2.5">
                <span className="text-[11px] font-semibold text-[#8898aa] uppercase tracking-wider leading-tight">
                  {stat.name}
                </span>
                <div className={`p-1.5 rounded-lg ${stat.iconBg} shrink-0`}>
                  <Icon className={`h-3.5 w-3.5 ${stat.iconColor}`} />
                </div>
              </div>
              <p className="text-xl font-bold tabular-nums tracking-tight text-[#0A2540] dark:text-[#e6edf3] leading-none">
                {stat.value}
              </p>
              {stat.subtitle && (
                <p className={`text-[11px] mt-1.5 font-medium ${stat.subtitleColor}`}>
                  {stat.subtitle}
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white dark:bg-[#161b22] rounded-xl border border-[#E6E9F0] dark:border-[#21262d] shadow-stripe p-5">
              <div className="flex items-center gap-4">
                <div className={`p-2.5 rounded-xl ${'iconBg' in stat ? stat.iconBg : 'bg-[#635BFF]/10'} dark:bg-white/5`}>
                  <Icon className={`h-5 w-5 ${'iconColor' in stat ? stat.iconColor : 'text-[#635BFF]'}`} />
                </div>
                <div>
                  <p className="text-xs font-medium text-[#8898aa] uppercase tracking-wider">{stat.name}</p>
                  <p className="text-lg font-semibold text-[#0A2540] dark:text-[#e6edf3] tabular-nums">{stat.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-[#161b22] rounded-xl border border-[#E6E9F0] dark:border-[#21262d] shadow-stripe p-6">
          <h3 className="text-sm font-medium text-[#8898aa] mb-3">Ingresos del mes actual</h3>
          <div className="text-2xl font-semibold text-[#0A2540] dark:text-[#e6edf3] tabular-nums">
            {Number(monthlyRevenue || 0).toFixed(2)} €
          </div>
          <p className="text-xs text-[#8898aa] mt-1">
            {new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
          </p>
        </div>

        <div className="bg-white dark:bg-[#161b22] rounded-xl border border-[#E6E9F0] dark:border-[#21262d] shadow-stripe p-6">
          <h3 className="text-sm font-medium text-[#8898aa] mb-3">IVA Trimestre {currentQuarter}</h3>
          <div className="text-2xl font-semibold text-[#635BFF] tabular-nums">
            {Number(quarterlyVAT || 0).toFixed(2)} €
          </div>
          <p className="text-xs text-[#8898aa] mt-1">
            {`Q${currentQuarter} ${currentDate.getFullYear()}`} · {getQuarterMonths(currentQuarter)}
          </p>
        </div>

        <div className="lg:col-span-1">
          <YearlyIncomeChart invoices={invoices} />
        </div>
      </div>

      {topClients.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] rounded-xl border border-[#E6E9F0] dark:border-[#21262d] shadow-stripe p-6">
          <h3 className="text-sm font-semibold text-[#0A2540] dark:text-[#e6edf3] mb-4">Principales clientes</h3>
          <div className="space-y-3">
            {topClients.map((clientStat, index) => (
              <div key={clientStat.client.id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                    index === 0 ? 'bg-amber-50 text-amber-700' :
                    index === 1 ? 'bg-slate-50 text-slate-600' :
                    'bg-orange-50 text-orange-700'
                  }`}>
                    {index + 1}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#0A2540] dark:text-[#e6edf3]">{clientStat.client.name}</p>
                    <p className="text-xs text-[#8898aa]">{clientStat.invoiceCount} facturas</p>
                  </div>
                </div>
                <p className="text-sm font-semibold text-[#0A2540] dark:text-[#e6edf3] tabular-nums">
                  {Number(clientStat.totalRevenue || 0).toFixed(2)} €
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}; 